import { useState, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";

const useUserAdmin = (userId) => {
  const [userProfile, setUserProfile] = useState({});
  const [shouldUserUpdate, updateUser] = useState(false);

  // only fetch when we have an authenticated session
  const [ready, setReady] = useState(false);

  // prevent duplicate calls (StrictMode / quick re-renders)
  const inFlight = useRef(false);

  // establish auth readiness and react to sign in/out
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setUserProfile({});
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // fetch the user profile once authed and when userId/refresh changes
  useEffect(() => {
    if (!ready || !userId) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check session
        const profile = await API.get(
          "AccountGatewayApi",
          `/profile/${userId}`
        );
        if (!cancelled) {
          setUserProfile(profile || {});
          updateUser(false);
        }
      } catch (err) {
        if (!cancelled) console.error("error fetching user profile", err);
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, userId, shouldUserUpdate]);

  return { userProfile, updateUser };
};

export default useUserAdmin;
